class SkCraftVideo extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-enhanced');

    this.querySelector('[data-video-play]')?.addEventListener('click', () => this.loadVideo());
    this.productForm = this.querySelector('[data-skcv-product-form]');
    this.productForm?.addEventListener('submit', this.handleAddToCart);
    document.addEventListener('shopify:product:select', this.handleProductSelect);
    this.setupReveal();
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener('submit', this.handleAddToCart);
    document.removeEventListener('shopify:product:select', this.handleProductSelect);
  }

  handleAddToCart = async (event) => {
    event.preventDefault();

    const button = this.querySelector('[data-skcv-add-to-cart]');
    const variantInput = this.querySelector('[data-skcv-variant-input]');
    const status = this.querySelector('[data-skcv-cart-status]');
    const variantFromUrl = new URL(window.location.href).searchParams.get('variant');
    const variantId = /^\d+$/.test(variantFromUrl || '')
      ? variantFromUrl
      : variantInput?.value || button?.dataset.variantId;

    if (!button || button.disabled || !variantId) return;

    const defaultLabel = button.dataset.defaultLabel || button.textContent.trim();
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Adding…';
    if (status) status.textContent = 'Adding product to cart.';

    try {
      const addResponse = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });

      const addedItem = await addResponse.json();
      if (!addResponse.ok) throw new Error(addedItem.description || addedItem.message || 'Unable to add this product.');

      let cart = null;
      try {
        const cartResponse = await fetch(this.dataset.cartJsonUrl || '/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (_error) {
        cart = null;
      }

      button.textContent = 'Added!';
      if (status) status.textContent = 'Product added to cart. Cart drawer opened.';

      if (cart && typeof window.refreshCartDrawer === 'function') {
        window.refreshCartDrawer(cart, addedItem, true);
      } else if (typeof window.refreshAndOpenKushaCartDrawer === 'function') {
        await window.refreshAndOpenKushaCartDrawer(button);
      } else {
        document.querySelector('[data-cart-open]')?.click();
      }

      window.setTimeout(() => {
        button.textContent = defaultLabel;
        button.disabled = false;
        button.classList.remove('is-loading');
      }, 1600);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add this product. Please try again.';
      button.textContent = defaultLabel;
      button.disabled = false;
      button.classList.remove('is-loading');
      if (status) status.textContent = message;
    }
  };

  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card') || !event.promise) return;

    event.promise
      .then(({ detail }) => {
        const productId = String(detail?.productId || '').split('/').pop();
        if (productId && productId !== this.dataset.productId) return;

        const variant = detail?.resource;
        const variantId = String(variant?.id || '').split('/').pop();
        if (!variantId) return;

        const button = this.querySelector('[data-skcv-add-to-cart]');
        const variantInput = this.querySelector('[data-skcv-variant-input]');
        if (!button || !variantInput) return;

        const available = variant.available !== false && variant.availableForSale !== false;
        variantInput.value = variantId;
        button.dataset.variantId = variantId;
        button.disabled = !available;
        button.classList.toggle('is-disabled', !available);
        button.textContent = available ? button.dataset.defaultLabel || 'Shop this Aasan' : 'Sold out';
      })
      .catch(() => {});
  };

  loadVideo() {
    const stage = this.querySelector('[data-video-stage]');
    const template = this.querySelector('template[data-video-template]');

    if (!stage || !template || this.hasAttribute('data-video-loaded')) return;

    stage.replaceChildren(template.content.cloneNode(true));
    this.setAttribute('data-video-loaded', '');

    const video = stage.querySelector('video');
    if (video) video.play().catch(() => {});
  }

  setupReveal() {
    const items = this.querySelectorAll('.skcv-reveal');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.dataset.animate !== 'true' || reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    items.forEach((item) => observer.observe(item));
  }
}

if (!customElements.get('sk-craft-video')) {
  customElements.define('sk-craft-video', SkCraftVideo);
}
